import type {
  TextFragment,
  TranscriptionEvent,
  SpeechEvent,
} from "../types/model.types";

export interface TranscriptManagerOptions {
  onTranscriptionEvent?: (
    event: TranscriptionEvent,
    fullTranscript: TranscriptionEvent[] | null,
  ) => void;
  enableFullTranscript?: boolean;
}

/**
 * Manages transcription events and maintains a full transcript history.
 * Handles the logic that was previously in the SDK player store.
 */
export class TranscriptManager {
  private _fullTranscript: TranscriptionEvent[] = [];
  private _lastSpeechEvent: SpeechEvent | null = null;
  private _lastSpeakerId: string | null = null;
  public options: TranscriptManagerOptions;

  constructor(options: TranscriptManagerOptions) {
    this.options = options;
  }

  /**
   * Processes a transcription event and updates the full transcript.
   * @param event - The transcription event to process
   */
  public processSpeechEvent(textFragment: TextFragment): void {
    const currentSpeakerId = textFragment.speakerLabel ?? "";

    // Only emit speaker change if speaker actually changed and is not empty
    if (currentSpeakerId && currentSpeakerId !== this._lastSpeakerId) {
      this._pushAndEmitTranscriptionEvent({
        type: "speakerChange",
        speakerId: currentSpeakerId,
      });
      this._lastSpeakerId = currentSpeakerId;
    }

    if (this.options.enableFullTranscript) {
      this._processSpeechEvent({
        type: "speech",
        textFragment: textFragment,
      });
    } else {
      this._pushAndEmitTranscriptionEvent({
        type: "speech",
        textFragment: textFragment,
      });
    }
  }

  public processAudioEvent(event: string): void {
    this._pushAndEmitTranscriptionEvent({
      type: "audioEvent",
      event: event,
    });
  }

  /**
   * Gets the current full transcript
   */
  public getFullTranscript(): TranscriptionEvent[] {
    return [...this._fullTranscript];
  }

  /**
   * Clears the full transcript
   */
  public clearTranscript(): void {
    this._fullTranscript = [];
    this._lastSpeechEvent = null;
    this._lastSpeakerId = null;
  }

  private _processSpeechEvent(event: SpeechEvent): void {
    const { _lastSpeechEvent } = this;

    if (_lastSpeechEvent && !_lastSpeechEvent.textFragment.isEndOfSentence) {
      // Update the text fragment in the last speech event
      const updatedEvent: SpeechEvent = {
        type: "speech",
        textFragment: {
          ..._lastSpeechEvent.textFragment,
          text:
            _lastSpeechEvent.textFragment.text + " " + event.textFragment.text,
          isEndOfSentence: event.textFragment.isEndOfSentence,
        },
      };
      // Create a new array to ensure mutability
      this._fullTranscript = [
        ...this._fullTranscript.slice(0, -1),
        updatedEvent,
      ];
      this._lastSpeechEvent = updatedEvent;
    } else {
      this._fullTranscript = [...this._fullTranscript, event];

      // Update last speech event to match what's stored
      this._lastSpeechEvent = event;
    }

    this._emitTranscriptionEvent(event);
  }

  private _pushAndEmitTranscriptionEvent(event: TranscriptionEvent): void {
    if (this.options.enableFullTranscript) {
      this._fullTranscript = [...this._fullTranscript, event];
    }
    this._emitTranscriptionEvent(event);
  }

  private _emitTranscriptionEvent(event: TranscriptionEvent): void {
    this.options.onTranscriptionEvent?.(
      event,
      this.options.enableFullTranscript ? [...this._fullTranscript] : null,
    );
  }
}
